"use client";

import { useRef, useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { ChevronRight, ChevronLeft } from "lucide-react";
import type { SignatureCake } from "@/lib/signatureCakes";
import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { CakeCard } from "@/components/cakes/CakeCard";
import { toCategorySlug, formatCategoryFallback } from "@/lib/cake-categories";

interface CakesGridCategoryProps {
  cakes: SignatureCake[];
  categoryName: string;
  staggerDelay?: number;
  prioritizeFirstImage?: boolean;
  isValentineEnabled?: boolean;
  isLegacyWebsiteEnabled?: boolean;
  linkPrefetch?: boolean;
  showDesktopScrollArrows?: boolean;
  /**
   * When true, render the section without the scroll-reveal opacity
   * animation. Use for above-the-fold categories so they are fully
   * visible on first paint (improves LCP).
   */
  skipReveal?: boolean;
  /** Analytics surface label, threaded down to each card. */
  surface?: string;
  /** Number of cakes rendered by earlier categories, for `position_overall`. */
  positionOffset?: number;
  /** Emit `cake_card_impression` for the cards in this section. */
  trackImpressions?: boolean;
}

export function CakesGridCategory({
  cakes,
  categoryName,
  staggerDelay = 0,
  prioritizeFirstImage = false,
  isValentineEnabled = false,
  isLegacyWebsiteEnabled = false,
  linkPrefetch = true,
  showDesktopScrollArrows = true,
  skipReveal = false,
  surface = "cakes_grid",
  positionOffset = 0,
  trackImpressions = false,
}: CakesGridCategoryProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  // Scroll reveal animation for the category section.
  // When skipReveal is true we disable the observer entirely so the
  // element is visible on first paint.
  const { ref: sectionRef, isVisible: observedVisible } = useScrollReveal({
    threshold: 0.1,
    rootMargin: "0px",
    once: true,
    enabled: !skipReveal,
  });
  // Above-the-fold sections must render opaque immediately.
  const sectionVisible = skipReveal ? true : observedVisible;

  useEffect(() => {
    if (!showDesktopScrollArrows) return;

    const checkScrollability = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        setCanScrollLeft(scrollLeft > 1);
      }
    };

    checkScrollability();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollability);
      window.addEventListener("resize", checkScrollability);
      return () => {
        container.removeEventListener("scroll", checkScrollability);
        window.removeEventListener("resize", checkScrollability);
      };
    }
  }, [cakes, showDesktopScrollArrows]);

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };

  // Use translated category name; fall back to formatted slug
  const tCategories = useTranslations("cakesGrid.categoryNames");
  const categorySlug = toCategorySlug(categoryName);
  const translated = tCategories.has(categorySlug as any)
    ? tCategories(categorySlug as any)
    : null;
  const displayName = translated ?? formatCategoryFallback(categoryName);

  // For above-the-fold categories, skip the opacity wrapper entirely so
  // SSR HTML paints the cakes immediately. The ref is still attached
  // (harmless when the observer is disabled) for DOM stability.
  const wrapperClass = skipReveal
    ? "cake-cat mx-auto mb-0 max-w-[1536px]"
    : `cake-cat mx-auto mb-0 max-w-[1536px] transition-opacity duration-200 ease-out ${
        sectionVisible ? "opacity-100" : "opacity-0"
      }`;

  return (
    <div
      ref={sectionRef as React.RefObject<HTMLDivElement>}
      className={wrapperClass}
      data-category={categorySlug}
      style={skipReveal ? undefined : { transitionDelay: `${staggerDelay}ms` }}
    >
      <div className="cake-cat-head py-8 md:py-10 px-4 sm:px-6 lg:px-8 text-center sm:text-left">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground relative inline-block mb-2">
          {displayName}
          <span
            className="absolute bottom-0 left-0 h-1 w-full bg-primary origin-left transition-transform duration-200 ease-out"
            style={{
              transform: sectionVisible ? "scaleX(1)" : "scaleX(0)",
              transitionDelay: `${staggerDelay + 200}ms`,
            }}
          />
        </h2>
      </div>

      <div
        className="cake-rail relative"
        onMouseEnter={showDesktopScrollArrows ? () => setIsHovered(true) : undefined}
        onMouseLeave={showDesktopScrollArrows ? () => setIsHovered(false) : undefined}
      >
        <div
          ref={scrollContainerRef}
          className="cake-scroller overflow-x-auto scrollbar-hide"
          style={{ scrollBehavior: "smooth" }}
        >
          <div className="cake-track flex gap-4 sm:gap-6 min-w-max px-4 sm:px-6 lg:px-8 pb-2">
            {cakes.map((cake, index) => (
              <CakeCard
                key={cake.id}
                cake={cake}
                category={categorySlug}
                positionInCategory={index}
                positionOverall={positionOffset + index}
                surface={surface}
                prioritizeImage={prioritizeFirstImage && index === 0}
                isValentineEnabled={isValentineEnabled}
                isLegacyWebsiteEnabled={isLegacyWebsiteEnabled}
                linkPrefetch={linkPrefetch}
                trackImpressions={trackImpressions}
              />
            ))}
          </div>
        </div>
        {showDesktopScrollArrows && isHovered && canScrollLeft && (
          <button
            onClick={handleScrollLeft}
            className={`cake-rail-arrow hidden md:flex absolute left-4 w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800 items-center justify-center shadow-lg hover:bg-gray-300 dark:hover:bg-gray-700 transition-all z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              sectionVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              transitionDelay: `${staggerDelay + 300}ms`,
            }}
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
        )}
        {showDesktopScrollArrows && isHovered && canScrollRight && (
          <button
            onClick={handleScrollRight}
            className={`cake-rail-arrow hidden md:flex absolute right-4 w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800 items-center justify-center shadow-lg hover:bg-gray-300 dark:hover:bg-gray-700 transition-all z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              sectionVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              transitionDelay: `${staggerDelay + 300}ms`,
            }}
            aria-label="Scroll right"
          >
            <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          </button>
        )}
      </div>
    </div>
  );
}
